import React, { useState } from 'react';

const LarryLineExplainer: React.FC = () => {
    const [expanded, setExpanded] = useState(true);

    return (
        <div className="larry-explainer">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>What is The Larry Line?</h2>
                <button className="explainer-toggle" onClick={() => setExpanded(!expanded)}>
                    {expanded ? 'Collapse' : 'Read More'}
                </button>
            </div>

            <p>
                Tanking is the biggest problem in professional sports. Every year, the worst teams in the NHL and NBA
                <span className="highlight"> lose on purpose</span> to get higher draft picks. Fans pay full price to watch
                their team try to lose. Players get benched so the team can fall further. Front offices openly tank,
                and everyone pretends it's "rebuilding."
            </p>

            {expanded && (
                <>
                    <p>
                        The current draft lottery was supposed to fix this, but it didn't. Teams still tank because
                        worse records mean better lottery odds. Going from 32nd to 30th still improves your chances
                        at the #1 pick. The incentive to lose is baked into the system.
                    </p>

                    <p>
                        <strong>The Larry Line is a simple fix:</strong> once a team is mathematically eliminated from
                        the playoffs, every game matters again. Instead of the worst team getting the best pick,
                        the team that <span className="highlight">tries the hardest after elimination</span> gets
                        the #1 pick.
                    </p>

                    <div className="explainer-steps">
                        <div className="explainer-step">
                            <span className="step-number">1</span>
                            <h3>Eliminated from Playoffs</h3>
                            <p>Team is mathematically eliminated and enters the Larry Pool</p>
                        </div>
                        <div className="explainer-step">
                            <span className="step-number">2</span>
                            <h3>Every Point Counts</h3>
                            <p>Post-elimination wins and points determine your draft position</p>
                        </div>
                        <div className="explainer-step">
                            <span className="step-number">3</span>
                            <h3>First Place = First Pick</h3>
                            <p>#1 in the Larry Pool gets the #1 overall draft pick</p>
                        </div>
                    </div>

                    <p>
                        Think about it: bad teams get eliminated <strong>earlier</strong>, which means they have
                        <strong> more games</strong> to earn points in the Larry Pool. That's the built-in advantage for
                        bad teams &mdash; they don't need lottery luck, they just need to compete. A team eliminated in
                        January has 40+ games to rack up points. A team eliminated in March has maybe 15.
                    </p>

                    <p>
                        Under The Larry Line, you either <strong>go for the playoffs</strong> or you <strong>go for
                        the #1 pick</strong>. There is zero incentive to tank. Every game matters. Every point matters.
                        Fans always have something to cheer for.
                    </p>

                    <div style={{ background: '#f0f4f8', padding: '16px 20px', borderRadius: 8, marginTop: 16 }}>
                        <p style={{ fontStyle: 'italic', color: '#555', marginBottom: 6, fontSize: '0.92rem' }}>
                            "Why is it called The Larry Line?"
                        </p>
                        <p style={{ color: '#1a1a2e', marginBottom: 4, fontSize: '0.92rem' }}>
                            No, it's not named after Larry Bird, Larry Robinson, or Larry Walker. The Larry Line is named
                            after a new baby named Larry &mdash; a fresh start, a clean slate, a new way of thinking
                            about the draft. No baggage, no history, just a better system.
                        </p>
                    </div>

                    <p style={{ fontWeight: 600, color: '#1a1a2e', marginTop: 16, marginBottom: 4 }}>
                        The Larry Line tracks what would happen if this system was real. Click NHL or NBA above to see
                        current standings and 5 years of historical draft comparisons.
                    </p>
                </>
            )}
        </div>
    );
};

export default LarryLineExplainer;
